import * as MapLibreGL from "maplibre-gl";
import {
  AttributionControl,
  NavigationControl,
  Popup,
  AddLayerObject,
  DataDrivenPropertyValueSpecification,
} from "maplibre-gl";
import type {
  Map,
  GeoJSONSource,
  LngLatLike,
  MapLayerMouseEvent,
} from "maplibre-gl";
import Spiderfy from "@nazka/map-gl-js-spiderfy";
import {
  getLanguageFromUrl,
  getDatasetId,
  encodeBase64,
  resolveAssetUrl,
} from "../../utils/window-utils";
import { getDatasetItem } from "../../services";

export const POPUP_CONTAINER_ID = "popup-container";

/** The level to which we zoom when jumping to a popup, on clicking a search result */
const POPUP_INITIAL_ZOOM = 15;

/** The zoom level at which colocated clusters can be spiderfied */
const SPIDERFY_ZOOM = 18;

/** Note: We ONLY USE INDEXES in this file. MapLibre doesn't know about IDs. Read architecture.md */
let popupIx: number | undefined;
let popup: Popup | undefined;
let tooltip: Popup | undefined;

let panelOpen: boolean = false;
let resultsPanelOpen: boolean = false;
let mapCenterOffsetPixels: [number, number] = [0, 0];

/**
 * We keep these values in this non-React file updated by our React MapWrapper component, since they
 * are used to calculate the offset when panning the map, so that markers are not hidden behind
 * panels.
 */
export const setPanelOpenValues = (panel: boolean, resultsPanel: boolean) => {
  panelOpen = panel;
  resultsPanelOpen = resultsPanel;

  // Re-calculate panel width for desktop and thus the map center offset
  const isDesktop = window.innerWidth >= 897;
  const PANEL_WIDTH = 375; // From CSS variable --panel-width-desktop
  let leftPanelWidth =
    isDesktop && panelOpen
      ? resultsPanelOpen
        ? PANEL_WIDTH * 2
        : PANEL_WIDTH
      : 0;
  mapCenterOffsetPixels = [leftPanelWidth / 2, 0];
};

/**
 * We need to offset latitude of the map centre slightly above a marker's location when opening a
 * popup so that it can be fully seen. I've calcluated that this exponential function gives a good
 * offset.
 */
const getMapCentreLatOffsetted = (lat: number, zoom: number) =>
  Math.min(90, lat + 87 * Math.exp(-0.704 * zoom));

const isLocationNear = (location: [number, number], map: Map) => {
  const currentZoom = map.getZoom();
  if (currentZoom < POPUP_INITIAL_ZOOM) {
    return false; // too far out to be considered "near", marker is likely to be clustered
  }

  const { _sw, _ne } = map.getBounds();
  const lngMargin = (_ne.lng - _sw.lng) / 2;
  const latMargin = (_ne.lat - _sw.lat) / 2;

  const nearBox = {
    swLng: _sw.lng - lngMargin,
    swLat: _sw.lat - latMargin,
    neLng: _ne.lng + lngMargin,
    neLat: _ne.lat + latMargin,
  };

  return (
    nearBox.swLng <= location[0] &&
    nearBox.swLat <= location[1] &&
    nearBox.neLng >= location[0] &&
    nearBox.neLat >= location[1]
  );
};

const getTooltip = (name: string): string =>
  `<div class="px-[0.75rem] py-2">${name}</div>`;

const disableRotation = (map: Map) => {
  map.dragRotate.disable();
  map.keyboard.disable();
  map.touchZoomRotate.disableRotation();
};

/**
 * Computes the bounding box of all features in the GeoJSON source and fits the map to those bounds,
 * with a bit of padding and accounting for the left panel on desktop. This is used to auto-zoom the
 * map when filters are applied, so that markers are visible and unclustered where possible.
 */
export const fitBoundsToFeatures = (map: Map) => {
  const source = map.getSource("items-geojson") as GeoJSONSource;
  if (!source) {
    console.warn("GeoJSON source not found, cannot fit bounds");
    return;
  }

  const data = source._data as GeoJSON.FeatureCollection<GeoJSON.Point>;
  if (!data || !data.features || data.features.length === 0) {
    console.log("No features to fit bounds to");
    return;
  }

  // If there's only one point, we can ease to that point without zooming since it's not clustered
  if (data.features.length === 1) {
    map.easeTo({
      center: data.features[0].geometry.coordinates as LngLatLike,
      duration: 1000,
      offset: mapCenterOffsetPixels,
    });
    console.log(
      `Fitted bounds to single point: ${data.features[0].geometry.coordinates}`,
    );
    return;
  }

  // Compute the bounding box of all features
  let minLng = 180;
  let maxLng = -180;
  let minLat = 90;
  let maxLat = -90;

  for (const feature of data.features) {
    const [lng, lat] = feature.geometry.coordinates;
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }

  // If all points are at the same location, zoom in to that cluster
  if (minLng === maxLng && minLat === maxLat) {
    // Apply offset for left panels if present
    map.easeTo({
      center: [minLng, minLat],
      zoom: POPUP_INITIAL_ZOOM,
      duration: 1000,
      offset: mapCenterOffsetPixels,
    });
    console.log(`Fitted bounds to single cluster: [${minLng}, ${minLat}]`);
    return;
  }

  // Add padding around the bounds in px so that markers are not at the very edge of the screen
  const basePadding = 150;
  const leftPadding = basePadding + mapCenterOffsetPixels[0] * 2;

  map.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    {
      padding: {
        top: basePadding,
        bottom: basePadding,
        left: leftPadding,
        right: basePadding,
      },
      duration: 1000,
      maxZoom: POPUP_INITIAL_ZOOM,
    },
  );

  console.log(
    `Fitted bounds to ${data.features.length} features: [[${minLng}, ${minLat}], [${maxLng}, ${maxLat}]]`,
  );
};

const openPopup = async (
  map: Map,
  itemIx: number,
  coordinates: LngLatLike,
  popupCreatedCallback: (itemIx: number) => void,
  popupClosedCallback: () => void,
  offset?: [number, number],
) => {
  if (popup?.isOpen() && popupIx === itemIx) {
    console.log(`Popup for item ${itemIx} already open`);
    return;
  }

  console.log(`Create marker popup for item @${itemIx}`);

  // Hide any visible tooltips when opening popup
  tooltip?.remove();

  // Shift the popup up a bit so it doesn't cover the marker
  const popupOffset: [number, number] = offset
    ? [offset[0], offset[1] - 20]
    : [0, -20];

  popup?.off("close", popupClosedCallback);
  popup?.remove();

  popupIx = itemIx;
  popup = new Popup({
    closeButton: false,
    maxWidth: "none",
    anchor: "bottom",
  })
    .setLngLat(coordinates)
    .setHTML(`<div id=${POPUP_CONTAINER_ID}></div>`)
    .addTo(map)
    .addClassName(`popup-ix-${itemIx}`)
    .setOffset(popupOffset)
    .on("close", popupClosedCallback);

  popupCreatedCallback(itemIx);
};

const onMarkerHover = async (
  map: Map,
  feature: GeoJSON.Feature<GeoJSON.Point>,
  offset?: [number, number],
) => {
  const datasetId = getDatasetId();
  if (!datasetId) {
    console.warn("No dataset ID available");
    return;
  }

  const coordinates = feature.geometry.coordinates.slice() as LngLatLike;
  const itemIx = feature.properties?.ix;

  if (itemIx === undefined) {
    console.warn("No item index found in feature properties");
    return;
  }

  // Only show tooltip when popup is not open
  if (popup?.isOpen() && popupIx === itemIx) {
    return;
  }

  try {
    const response = await getDatasetItem({
      params: { datasetId, datasetItemIdOrIx: encodeBase64(`@${itemIx}`) },
      query: { returnProps: ["name"] },
    });

    if (response.status === 200 && response.body.name) {
      const name = response.body.name as string;

      // Shift the tooltip up a bit so it doesn't cover the marker
      const popupOffset: [number, number] = offset
        ? [offset[0], offset[1] - 30]
        : [0, -30];

      tooltip?.remove();
      tooltip = new Popup({
        closeButton: false,
        maxWidth: "none",
        className: "marker-tooltip",
        anchor: "bottom",
      })
        .setLngLat(coordinates)
        .setHTML(getTooltip(name))
        .addTo(map)
        .setOffset(popupOffset);
    }
  } catch (error) {
    console.error("Error fetching item name for tooltip:", error);
  }
};

/**
 * Set up the sources and layers of the MapLibreGL map instance.
 */
export const createMap = (
  popupCreatedCallback: (itemIx: number) => void,
  popupClosedCallback: () => void,
  mapCreated: () => void,
  mapConfig?: {
    mapBounds?: [[number, number], [number, number]];
  },
  markerIcons: string[] = ["default"],
): Map => {
  const initialBounds = mapConfig?.mapBounds ?? [
    [-169, -49.3],
    [189, 75.6],
  ];

  console.log("Map bounds", initialBounds);

  const map = new MapLibreGL.Map({
    container: "map-container",
    style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${import.meta.env.VITE_MAPTILER_API_KEY}`,
    minZoom: 1.45,
    maxZoom: 18,
    bounds: initialBounds,
    attributionControl: false,
  });

  map.on("load", async () => {
    map.addSource("items-geojson", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
      buffer: 0,
      cluster: true,
      clusterMaxZoom: 19,
      clusterRadius: 60,
    });

    map.addLayer({
      id: "clusters",
      type: "circle",
      source: "items-geojson",
      filter: ["has", "point_count"],
      paint: {
        // Use step expressions (https://docs.mapbox.com/style-spec/reference/expressions/#step)
        "circle-color": [
          "step",
          ["get", "point_count"],
          "#51bbd6",
          100,
          "#f1f075",
          750,
          "#f28cb1",
        ],
        "circle-radius": 20,
      },
    });

    map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "items-geojson",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
    });

    const markerList = [];
    let index = 0;

    for (let marker of markerIcons) {
      // markerIcons entries are either bundled names (e.g. "dotcoop",
      // "default") that resolve to the front-end's static assets, or full
      // URLs / `dataset:` references that are served from the dataset
      const imgSrc =
        /^(https?:)?\/\//.test(marker) || marker.startsWith("dataset:")
          ? resolveAssetUrl(marker)
          : `./assets/markers/${marker}.png`;
      if (!imgSrc) continue;
      const image = await map.loadImage(imgSrc);
      const markerName = "marker-" + index;
      map.addImage(markerName, image.data);
      markerList.push(index++);
      markerList.push(markerName);
    }

    const markerLayout = {
      "icon-image": [
        "match",
        ["get", "custom_marker_id"],
        ...markerList,
        `marker-${markerIcons.length - 1}`, // assumes the final marker in the marker list is the default marker
      ],
      "icon-anchor": "bottom",
    };

    map.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "items-geojson",
      filter: ["!", ["has", "point_count"]],
      layout:
        markerLayout as unknown as DataDrivenPropertyValueSpecification<string>,
    } as AddLayerObject);

    const spiderfy = new Spiderfy(map, {
      onLeafClick: (
        feature: GeoJSON.Feature<GeoJSON.Point>,
        _e: MapLayerMouseEvent,
        leafOffset: [number, number],
      ) => {
        const coordinates = feature.geometry.coordinates.slice();
        const itemIx = feature.properties?.ix;

        if (popup?.isOpen() && popupIx === itemIx) {
          console.log(
            `Popup for item @${itemIx} already open so toggle closed`,
          );
          popup?.remove();
          popupIx = undefined;
          popup = undefined;
          return;
        }

        popup?.off("close", popupClosedCallback);

        // Ease to new marker then open popup
        map
          .easeTo({
            center: [
              coordinates[0],
              getMapCentreLatOffsetted(coordinates[1], map.getZoom()),
            ],
            offset: mapCenterOffsetPixels,
          })
          .once("moveend", () => {
            openPopup(
              map,
              itemIx,
              coordinates as LngLatLike,
              popupCreatedCallback,
              popupClosedCallback,
              leafOffset,
            );
          });
      },
      onLeafHover: (
        feature: GeoJSON.Feature<GeoJSON.Point>,
        _e: MapLayerMouseEvent,
        leafOffset: [number, number] | undefined,
      ) => {
        if (feature) {
          onMarkerHover(map, feature, leafOffset);
          map.getCanvas().style.cursor = "pointer";
        } else {
          tooltip?.remove();
          map.getCanvas().style.cursor = "";
        }
      },
      minZoomLevel: SPIDERFY_ZOOM,
      zoomIncrement: 0,
      closeOnLeafClick: false,
      spiderLeavesLayout:
        markerLayout as unknown as DataDrivenPropertyValueSpecification<string>,
    });
    spiderfy.applyTo("clusters");

    type ClusterClickEvent = MapLibreGL.MapMouseEvent & {
      itemIx?: number;
      openPopupRecursive?: boolean;
    };
    // inspect a cluster on click
    map.on("click", "clusters", async (e: ClusterClickEvent) => {
      const clusterFeature: GeoJSON.Feature<GeoJSON.Point> =
        map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        })[0] as GeoJSON.Feature<GeoJSON.Point>;

      const source = map.getSource("items-geojson") as GeoJSONSource;
      const clusterId = clusterFeature.properties?.cluster_id;
      const pointCount = clusterFeature.properties?.point_count ?? 1;

      if (clusterId === undefined) {
        return;
      }

      // Get all the leaves in a cluster
      const allLeaves: GeoJSON.Feature<GeoJSON.Point>[] =
        (await source.getClusterLeaves(
          clusterId,
          pointCount,
          0,
        )) as GeoJSON.Feature<GeoJSON.Point>[];

      // Coordinates of the first leaf in the cluster to check for colocation
      const firstLeaf = allLeaves[0];
      const firstLeafCoordinates = firstLeaf?.geometry.coordinates;

      if (!firstLeafCoordinates) {
        return;
      }

      // Check whether markers in cluster share the same coordinates
      const isColocated = allLeaves.every(
        (leaf) =>
          leaf.geometry.coordinates[0] === firstLeafCoordinates[0] &&
          leaf.geometry.coordinates[1] === firstLeafCoordinates[1],
      );

      const clusterExpansionZoom =
        await source.getClusterExpansionZoom(clusterId);

      // Colocated clusters zoom directly to spiderfy level
      if (isColocated && map.getZoom() < SPIDERFY_ZOOM) {
        const targetCoordinates = firstLeaf.geometry.coordinates as LngLatLike;

        map
          .easeTo({
            center: targetCoordinates,
            offset: mapCenterOffsetPixels,
            zoom: SPIDERFY_ZOOM,
            duration: 1000,
          })
          .once("moveend", () => {
            const point = map.project(targetCoordinates);

            // Synthetic click at zoom level to expand the spiderfy cluster
            map.fire("click", {
              lngLat: MapLibreGL.LngLat.convert(targetCoordinates),
              point,
            } as MapLibreGL.MapMouseEvent);
          });
        return;
      }

      if (map.getZoom() < SPIDERFY_ZOOM && clusterExpansionZoom <= SPIDERFY_ZOOM) {
        map.flyTo({
          center: firstLeaf.geometry.coordinates as LngLatLike,
          offset: mapCenterOffsetPixels,
          zoom: clusterExpansionZoom ?? undefined,
          duration: 500,
        });
      }

      //this click came from the flyToAndOpenPopupRecursive function
      if (e.openPopupRecursive) {
        const { leaves } = spiderfy.spiderifiedCluster;

        const index = leaves.findIndex(
          (leaf: any) => leaf.properties.ix === e.itemIx,
        );

        const totalPoints = leaves.length;

        const theta = (Math.PI * 2) / totalPoints;
        const angle = theta * index;

        const legLength =
          totalPoints <= 10 ? 50 : 50 + (index * (Math.PI * 2 * 2.2)) / angle;
        const _x = legLength * Math.cos(angle);
        const _y = legLength * Math.sin(angle);

        /*
        openPopup(
          map,
          e.itemIx as number,
          e.lngLat,
          popupCreatedCallback,
          popupClosedCallback,
          [x, y],
        );*/
      }
    });

    // When a click event occurs on a feature in the unclustered-point layer, open a popup at the
    // location of the feature, with description HTML from its properties.
    map.on("click", "unclustered-point", (e: MapLayerMouseEvent) => {
      if (e.features) {
        const feature = e.features[0] as GeoJSON.Feature<GeoJSON.Point>;
        const coordinates = feature.geometry.coordinates.slice();
        const itemIx = feature.properties?.ix;

        if (popup?.isOpen() && popupIx === itemIx) {
          console.log(`Popup for item ${itemIx} already open so toggle closed`);
          popup?.remove();
          popupIx = undefined;
          popup = undefined;
          return;
        }

        popup?.off("close", popupClosedCallback);

        // Ease to new marker then open popup
        map
          .easeTo({
            center: [
              coordinates[0],
              getMapCentreLatOffsetted(coordinates[1], map.getZoom()),
            ],
            offset: mapCenterOffsetPixels,
          })
          .once("moveend", () => {
            openPopup(
              map,
              itemIx,
              coordinates as LngLatLike,
              popupCreatedCallback,
              popupClosedCallback,
            );
          });
      }
    });

    map.on("openPopup", async ({ itemIx, location }) => {
      if (popup?.isOpen() && popupIx === itemIx) return;

      if (location === null) {
        console.error("This shouldn't happen");
        return;
      }

      // Remove previous popup - remove listener to prevent looping back and confusing React code
      popup?.off("close", popupClosedCallback);
      popup?.remove();
      popupIx = undefined;
      popup = undefined;

      if (isLocationNear(location, map)) {
        map.panTo(
          [location[0], getMapCentreLatOffsetted(location[1], map.getZoom())],
          { offset: mapCenterOffsetPixels },
        );
      } else {
        map.flyTo({
          center: [
            location[0],
            getMapCentreLatOffsetted(location[1], POPUP_INITIAL_ZOOM),
          ],
          offset: mapCenterOffsetPixels,
          duration: 0,
          zoom: POPUP_INITIAL_ZOOM,
        });
      }

      openPopup(
        map,
        itemIx,
        location,
        popupCreatedCallback,
        popupClosedCallback,
      );
    });

    map.on("closeAllPopups", () => {
      // The React code knows we're closing this popup - remove listener to prevent loop
      popup?.off("close", popupClosedCallback);
      popup?.remove();
      popupIx = undefined;
      popup = undefined;
    });

    map.on("zoomend", () => {
      console.log("Zoom level", map.getZoom());

      if (popup?.isOpen()) {
        const ix = Array.from(popup?._container.classList)
          .find((c: any) => c.startsWith("popup-ix-"))
          ?.replace("popup-ix-", "");
        const visibleFeatureIxs = map
          .queryRenderedFeatures(undefined, {
            layers: ["unclustered-point"],
          })
          .map((f) => f?.properties?.ix);

        if (!ix || !visibleFeatureIxs.includes(ix)) {
          // close the popup if the feature is no longer visible
          popup.remove();
        }
      }
    });

    map.on("moveend", () => {
      // console.debug("zoom / bounds", map.getZoom(), map.getBounds());
    });

    map.on("zoomstart", () => {
      spiderfy.unspiderfyAll();
    });

    map.on("mouseenter", "clusters", () => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "clusters", () => {
      map.getCanvas().style.cursor = "";
    });
    map.on("mouseenter", "unclustered-point", (e: any) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features[0];
      onMarkerHover(map, feature);
    });
    map.on("mouseleave", "unclustered-point", () => {
      tooltip?.remove();
      map.getCanvas().style.cursor = "";
    });
    // Cursor pointer on spiderfied cluster
    map.on("mouseenter", "spiderfied-cluster", () => {
      map.getCanvas().style.cursor = "pointer";
    });

    map.on("changeLanguage", ({ language }) => {
      const oldStyle = map.getStyle();
      const newStyle = JSON.stringify(oldStyle, (_key, val) => {
        if (typeof val === "string") {
          return val.replaceAll("name:en", `name:${language.toLowerCase()}`);
        }
        return val;
      });
      map.setStyle(JSON.parse(newStyle));
      console.log("Set MapLibre GL language to", language);
    });

    map.fire("changeLanguage", { language: getLanguageFromUrl() });

    map.addControl(new AttributionControl({ compact: true }), "top-right");
    map.addControl(new NavigationControl(), "top-right");
    disableRotation(map);

    console.log("MapLibre map created");
    mapCreated();
  });

  return map;
};
